import { useState, useMemo, useEffect } from 'react';
import {
  Flex,
  Box,
  Input,
  FormControl,
  FormErrorMessage,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  InputRightAddon,
  Icon,
  Tooltip,
  InputProps,
} from '@chakra-ui/react';
import { KText } from '../../text';
import { IconType } from 'react-icons';

// TODO: Change Icons once kula-icons new version published
import { GoInfo } from 'react-icons/go';
import { IoMdClose } from 'react-icons/io';

const defaultInputStyles = {
  borderColor: 'secondary-100',
  boxShadow: '0 0 0 1px secondary-100',
};

const errorInputStyles = {
  boxShadow: 'none !important',
  border: '1px solid #C03744 !important',
  color: '#C03744 !important',
};

const errorFocusStyles = {
  bg: 'pastel-1-light',
  outline: 'none',
  boxShadow: 'none',
};

const focusStyles = {
  bg: 'secondary-25',
  border: '1px solid #BFC3DF !important',
  outline: 'none',
  boxShadow: 'none',
};

interface KInputProps extends Omit<InputProps, 'onChange'> {
  label?: string;
  isRequired?: boolean;
  labelProps?: any;
  leftIcon?: IconType;
  rightIcon?: IconType;
  maxLength?: number;
  error?: any;
  info?: string;
  additionalInfo?: string;
  isDisabled?: boolean;
  clear?: boolean;
  onChange?: (_event: React.ChangeEvent<HTMLInputElement>) => void;
  value?: any;
  props?: any;
  endAddon?: React.ReactNode;
  CustomRightElement?: any;
  beforeMaxLength?: boolean;
  customMaxLengthProps?: any;
  customRightElementProps?: any;
  endAddonWrapperProps?: React.ComponentProps<typeof InputRightAddon>;
  CustomLeftElement?: any;
  customLeftElementProps?: any;
  AfterInputGroupContent?: React.ComponentType<any>;
  fieldType?: string;
  min?: number;
}

/**
 * A customizable input component that supports labels, icons, validation, and more
 * @param {KInputProps} props - The component props
 * @returns {JSX.Element} The rendered input component
 */
export const KInput: React.FC<KInputProps> = ({
  id,
  label,
  isRequired = false,
  labelProps,
  leftIcon,
  rightIcon,
  maxLength,
  error,
  info,
  additionalInfo,
  isDisabled = false,
  endAddon,
  endAddonWrapperProps = {},
  clear,
  CustomRightElement,
  beforeMaxLength = true,
  onChange = (_event) => {},
  value,
  customRightElementProps,
  CustomLeftElement,
  customLeftElementProps,
  AfterInputGroupContent,
  ...props
}) => {
  const { customMaxLengthProps = {}, fieldType, min = 0 } = props;
  const [charCount, setCharCount] = useState(String(value)?.length);
  const [customValue, setCustomValue] = useState(value);
  useEffect(() => {
    setCustomValue(value);
  }, [value]);

  const getElementColor = useMemo(() => {
    if (error) return 'error-200';
    if (isDisabled) return 'secondary-300';
    return undefined;
  }, [error, isDisabled]);

  const handleOnChange = (event) => {
    const newValue = event.target.value;

    // For number inputs, manually enforce maxLength since HTML doesn't support it natively
    if (
      fieldType === 'number' &&
      maxLength &&
      String(newValue).length > maxLength
    ) {
      return; // Don't update if exceeds maxLength
    }

    setCustomValue(newValue);
    if (maxLength) setCharCount(String(newValue).length);
    const inputElement = event.target;
    if (error) {
      inputElement.style.backgroundColor = 'white';
    }
    onChange(event);
  };

  const handleClear = () => {
    setCustomValue('');
    setCharCount(0);
    const event = {
      target: { value: '' },
    } as React.ChangeEvent<HTMLInputElement>;
    onChange(event);
  };

  const calculateRightPadding = useMemo(() => {
    let padding = 12;
    if (rightIcon) padding += 26;
    if (clear && customValue) padding += 26;
    if (maxLength) {
      const counterWidth = String(maxLength).length * 8 + 30;
      padding += counterWidth;
    }
    return `${padding}px`;
  }, [rightIcon, clear, customValue, maxLength]);

  const calculateLeftPadding = useMemo(() => {
    let padding = 12;
    if (CustomLeftElement) padding += 90;
    if (leftIcon) padding += 40;
    return `${padding}px`;
  }, [CustomLeftElement, leftIcon]);

  return (
    <>
      <FormControl
        isRequired={isRequired}
        isInvalid={!!error}
        isDisabled={isDisabled}
      >
        {label && (
          <Flex id={id} gap="4px" alignItems="center" mb="6px">
            <Flex gap="4px">
              <KText
                variant="heading_4"
                color={getElementColor}
                {...labelProps}
              >
                {label}
              </KText>
              {isRequired && <KText color={'error-200'}>*</KText>}
            </Flex>
            {info && (
              <Tooltip
                label={info}
                arrowSize={8}
                hasArrow={true}
                placement="top"
                variant="default"
              >
                <Box>
                  <Icon
                    // @ts-expect-error - GoInfo is an IconType
                    as={GoInfo}
                    w="16px"
                    h="16px"
                    cursor="pointer"
                    color={getElementColor}
                  />
                </Box>
              </Tooltip>
            )}
          </Flex>
        )}
        <InputGroup>
          {CustomLeftElement && (
            <InputLeftElement
              pointerEvents="none"
              mt="2px"
              left="0px"
              {...customLeftElementProps}
              children={<CustomLeftElement />}
            ></InputLeftElement>
          )}
          {leftIcon && (
            <InputLeftElement
              pointerEvents="none"
              mt="2px"
              children={
                <Icon
                  // @ts-expect-error - leftIcon is an IconType
                  as={leftIcon}
                  w="18px"
                  h="18px"
                  color={getElementColor}
                />
              }
            />
          )}
          <Input
            h="44px"
            {...props}
            value={customValue}
            maxLength={maxLength}
            min={min}
            onChange={handleOnChange}
            type={fieldType}
            sx={{
              ...(error ? errorInputStyles : defaultInputStyles),
              paddingRight: calculateRightPadding,
              paddingLeft: calculateLeftPadding,
              color: error ? '#C03744 !important' : 'secondary-700',
              ...(props.style || {}),
            }}
            _invalid={error ? errorInputStyles : defaultInputStyles}
            _placeholder={{
              color: getElementColor || 'secondary-500',
            }}
            _focus={error ? errorFocusStyles : focusStyles}
            onWheel={(e) => {
              if (fieldType === 'number') {
                e.currentTarget.blur();
              }
            }}
          />
          {endAddon && (
            <InputRightAddon
              h="44px"
              border="none"
              bg="transparent"
              pl="8px"
              pr="12px"
              {...endAddonWrapperProps}
            >
              {endAddon}
            </InputRightAddon>
          )}

          {CustomRightElement && beforeMaxLength && (
            <InputRightElement
              mt="2px"
              w="fit-content"
              right="60px"
              {...customRightElementProps}
              children={<CustomRightElement />}
            />
          )}

          {(rightIcon || clear || maxLength) && (
            <InputRightElement
              mt="2px"
              w="fit-content"
              right="12px"
              {...customMaxLengthProps}
              children={
                <Flex gap="8px" alignItems="center">
                  {rightIcon && (
                    <Icon
                      // @ts-expect-error - rightIcon is an IconType
                      as={rightIcon}
                      w="18px"
                      h="18px"
                      color={getElementColor}
                    />
                  )}
                  {!isDisabled && clear && customValue && (
                    <Icon
                      // @ts-expect-error - IoMdClose is an IconType
                      as={IoMdClose}
                      w="18px"
                      h="18px"
                      cursor="pointer"
                      onClick={handleClear}
                      color={getElementColor}
                    />
                  )}
                  {maxLength && (
                    <KText
                      variant="small_text"
                      textAlign="right"
                      whiteSpace="nowrap"
                      color={getElementColor}
                    >
                      {charCount}/{maxLength}
                    </KText>
                  )}
                </Flex>
              }
            />
          )}
        </InputGroup>
        {additionalInfo && (
          <KText variant="paragraph" mt="6px">
            {additionalInfo}
          </KText>
        )}
        {error?.length > 0 && (
          <FormErrorMessage color="error-200">{error}</FormErrorMessage>
        )}
      </FormControl>
      {AfterInputGroupContent && <AfterInputGroupContent />}
    </>
  );
};
